// Configurable dashboard built on react-grid-layout. Widgets are draggable
// (whole tile in edit-mode) and resizable via the bottom-right corner.
// The grid is controlled — the parent owns the PersistedLayout and decides
// where to persist it (current user via API, or a template via admin).

import { Plus, Settings2, X } from 'lucide-react'
import { useMemo, useState } from 'react'
// react-grid-layout 2.x split exports — the legacy entrypoint still ships the
// classic ReactGridLayout component + WidthProvider HOC we want here. The new
// hook-based API would force a bigger refactor for no UX win right now.
import RGL, { type LayoutItem, WidthProvider } from 'react-grid-layout/legacy'
import 'react-grid-layout/css/styles.css'
// react-resizable's CSS is bundled via react-grid-layout's transitive dep
// and pnpm doesn't hoist it — instead the minimal styles we need (handle
// position, resizable container) live inline in index.css.
import { useIsMobile, useMediaQuery } from '../../lib/use-media-query'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'
import { listWidgets, WidgetShell, type WidgetType, widgetDef } from './widget-registry'

const ResponsiveGridLayout = WidthProvider(RGL)
/** Column counts per breakpoint:
 *  - Phone   (<640px): 1 column  — single-column stacked layout
 *  - Tablet  (640–1024px): 2 columns — side-by-side pairs
 *  - Desktop (>1024px): 12 columns — full grid */
const COLS_PHONE = 4
const COLS_TABLET = 8
const COLS_DESKTOP = 12
const ROW_HEIGHT = 40

export interface WidgetInstance {
  i: string // unique instance id (also rgl key)
  type: WidgetType
  x: number
  y: number
  w: number
  h: number
  /** Per-instance config blob — opaque to the grid, owned by the widget. */
  config?: Record<string, unknown>
}

export interface PersistedLayout {
  version: 1
  /** Desktop-Layout — Master, gilt als Default für alle Breakpoints. */
  widgets: WidgetInstance[]
  /** Optional Mobile-Layout (<640px). Wenn nicht gesetzt → Auto-Stack aus
   *  Desktop. Wird beim ersten Drag im Mobile-EditMode angelegt. */
  widgetsMobile?: WidgetInstance[]
  /** Optional Tablet-Layout (640–1024px). Analog zu Mobile. */
  widgetsTablet?: WidgetInstance[]
}

export const EMPTY_LAYOUT: PersistedLayout = { version: 1, widgets: [] }

interface DashboardGridProps {
  /** Current layout. Controlled. */
  value: PersistedLayout
  /** Called whenever the layout changes (drag, resize, add, remove, config). */
  onChange: (next: PersistedLayout) => void
  /** Optional banner on top — used by the dashboard page to show a
   * "von Vorlage XYZ" hint until the user customises. */
  banner?: React.ReactNode
  /** Widget types the user's role allows. Null/undefined = all allowed. */
  allowedWidgets?: Record<string, boolean> | null
  /** Controlled edit mode — when provided, hides the built-in toolbar. */
  editMode?: boolean
  onEditModeChange?: (v: boolean) => void
  pickerOpen?: boolean
  onPickerOpenChange?: (v: boolean) => void
}

export function DashboardGrid({
  value,
  onChange,
  banner,
  allowedWidgets,
  editMode: editModeProp,
  onEditModeChange,
  pickerOpen: pickerOpenProp,
  onPickerOpenChange,
}: DashboardGridProps) {
  const state = value
  const setState = (next: PersistedLayout | ((prev: PersistedLayout) => PersistedLayout)) => {
    const computed = typeof next === 'function' ? next(state) : next
    onChange(computed)
  }
  const [editModeInternal, setEditModeInternal] = useState(false)
  const [pickerOpenInternal, setPickerOpenInternal] = useState(false)

  const controlled = editModeProp !== undefined
  const editMode = controlled ? editModeProp : editModeInternal
  const setEditMode = controlled
    ? (v: boolean | ((p: boolean) => boolean)) => {
        const next = typeof v === 'function' ? v(editMode) : v
        onEditModeChange?.(next)
      }
    : setEditModeInternal
  const pickerOpen = controlled ? (pickerOpenProp ?? false) : pickerOpenInternal
  const setPickerOpen = controlled
    ? (v: boolean) => {
        onPickerOpenChange?.(v)
      }
    : setPickerOpenInternal

  // Responsive column count — adapts layout to phone / tablet / desktop.
  const isPhone = useIsMobile()
  const isTablet = useMediaQuery('(min-width: 640px) and (max-width: 1024px)')
  const cols = isPhone ? COLS_PHONE : isTablet ? COLS_TABLET : COLS_DESKTOP
  const breakpoint: 'mobile' | 'tablet' | 'desktop' = isPhone
    ? 'mobile'
    : isTablet
      ? 'tablet'
      : 'desktop'

  // Welcher widgets-Bucket liefert Positionierung für diesen Breakpoint?
  // Desktop ist Master; Mobile/Tablet fallen auf Desktop zurück bis sie
  // ihren eigenen Layout-Override haben.
  const positionWidgets =
    (breakpoint === 'mobile' && state.widgetsMobile) ||
    (breakpoint === 'tablet' && state.widgetsTablet) ||
    state.widgets

  // Aber die Liste der Widgets (welche existieren überhaupt) kommt IMMER
  // aus dem Desktop-Bucket — Mobile/Tablet sind nur Positions-Overrides.
  // Match per `i`-Key zurück auf den Desktop-Bucket.
  const positionByI = new Map(positionWidgets.map((w) => [w.i, w]))

  // Hat dieser Breakpoint einen eigenen Override gespeichert?
  // Wenn ja: Position-Werte direkt nutzen, kein Auto-Stack-Clamp.
  // Wenn nein: Default-Adaption (alles zu x=0 stacken bei Mobile/Tablet).
  const hasOwnLayout = positionWidgets !== state.widgets
  const rglLayout: LayoutItem[] = useMemo(
    () =>
      state.widgets.map((master) => {
        const w = positionByI.get(master.i) ?? master
        const def = widgetDef(w.type)
        const locked = def?.resizable === false
        const isSmall = (def?.minSize.w ?? 2) <= 1
        const effectiveW = hasOwnLayout
          ? Math.min(w.w, cols)
          : isSmall
            ? Math.min(w.w, cols)
            : Math.min(cols < COLS_DESKTOP ? cols : w.w, cols)
        const effectiveX = hasOwnLayout
          ? Math.min(w.x, cols - effectiveW)
          : isSmall
            ? Math.min(w.x, cols - effectiveW)
            : cols < COLS_DESKTOP
              ? 0
              : Math.min(w.x, cols - effectiveW)
        const phoneMinH =
          isPhone && !isSmall ? Math.max(def?.minSize.h ?? 2, 3) : (def?.minSize.h ?? 2)
        return {
          i: w.i,
          x: effectiveX,
          y: w.y,
          w: effectiveW,
          h: Math.max(w.h, phoneMinH),
          minW: Math.min(def?.minSize.w ?? 2, cols),
          minH: phoneMinH,
          ...(locked
            ? {
                maxW: Math.min(def?.minSize.w ?? 1, cols),
                maxH: Math.max(def?.minSize.h ?? 1, phoneMinH),
                isResizable: false,
              }
            : {}),
        }
      }),
    [state.widgets, positionByI, hasOwnLayout, cols, isPhone],
  )

  function handleLayoutChange(next: readonly LayoutItem[]) {
    if (!editMode) return
    setState((prev) => {
      // Welcher Bucket wird beschrieben? Auf Mobile/Tablet pflegen wir den
      // jeweiligen Override; Desktop bleibt der Master.
      const key: 'widgets' | 'widgetsMobile' | 'widgetsTablet' =
        breakpoint === 'mobile'
          ? 'widgetsMobile'
          : breakpoint === 'tablet'
            ? 'widgetsTablet'
            : 'widgets'
      // Erste Bearbeitung in Mobile/Tablet → Override aus Desktop initialisieren.
      const base: WidgetInstance[] = prev[key] ?? prev.widgets
      const updated = base.map((w) => {
        const u = next.find((n) => n.i === w.i)
        return u ? { ...w, x: u.x, y: u.y, w: u.w, h: u.h } : w
      })
      return { ...prev, [key]: updated }
    })
  }

  function addWidget(type: WidgetType) {
    const def = widgetDef(type)
    if (!def) return
    const maxY = state.widgets.reduce((m, w) => Math.max(m, w.y + w.h), 0)
    const id = `${type}-${Date.now().toString(36)}`
    const newW: WidgetInstance = {
      i: id,
      type,
      x: 0,
      y: maxY,
      w: def.defaultSize.w,
      h: def.defaultSize.h,
    }
    setState((prev) => ({
      ...prev,
      widgets: [...prev.widgets, newW],
      // In bestehende Mobile/Tablet-Overrides mit aufnehmen, sonst bleiben sie
      // ohne das neue Widget zurück.
      widgetsMobile: prev.widgetsMobile ? [...prev.widgetsMobile, newW] : prev.widgetsMobile,
      widgetsTablet: prev.widgetsTablet ? [...prev.widgetsTablet, newW] : prev.widgetsTablet,
    }))
    setPickerOpen(false)
    setEditMode(true)
  }

  function removeWidget(id: string) {
    setState((prev) => ({
      ...prev,
      widgets: prev.widgets.filter((w) => w.i !== id),
      widgetsMobile: prev.widgetsMobile?.filter((w) => w.i !== id),
      widgetsTablet: prev.widgetsTablet?.filter((w) => w.i !== id),
    }))
  }

  function updateConfig(id: string, next: Record<string, unknown>) {
    // Config bleibt am Master (Desktop-widgets); Overrides tragen nur Position.
    setState((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) => (w.i === id ? { ...w, config: next } : w)),
    }))
  }

  return (
    <div className="space-y-4">
      {banner}
      {!controlled && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant={editMode ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setEditMode((v: boolean) => !v)}
          >
            <Settings2 className="h-4 w-4" />
            {editMode ? 'Bearbeitung beenden' : 'Layout bearbeiten'}
          </Button>
          <Button size="sm" onClick={() => setPickerOpen(true)}>
            <Plus className="h-4 w-4" /> Widget hinzufügen
          </Button>
        </div>
      )}

      {state.widgets.length === 0 ? (
        // Empty-State mit echtem CTA — der Verweis „Klicke »Widget
        // hinzufügen«" lief mobil ins Leere (dort ist der Header-Button
        // ein unbeschriftetes „+").
        <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center space-y-4">
          <p className="text-text-secondary">Dein Dashboard ist noch leer.</p>
          <Button size="sm" onClick={() => setPickerOpen(true)}>
            <Plus className="h-4 w-4" /> Widget hinzufügen
          </Button>
        </div>
      ) : (
        <div className="relative">
          {/* Edit-mode dotted-paper guide: a single dot at the top-left of
              each cell (= at every grid node). Horizontal stride matches the
              column width including the 12-px gap, vertical stride is one
              row + gap. Pointer-transparent and behind RGL. */}
          {editMode && (
            <div
              className="absolute inset-0 pointer-events-none -z-0"
              aria-hidden="true"
              style={{
                backgroundImage:
                  'radial-gradient(circle, rgb(var(--c-border) / 0.35) 1px, transparent 1.5px)',
                backgroundSize: `calc((100% + 12px) / ${cols}) ${ROW_HEIGHT + 12}px`,
                backgroundPosition: '0 0',
                backgroundRepeat: 'repeat',
              }}
            />
          )}
          <div
            style={{ touchAction: editMode ? 'none' : 'auto' }}
            onTouchMove={editMode ? (e) => e.stopPropagation() : undefined}
          >
            <ResponsiveGridLayout
              className={`layout relative z-10 ${editMode ? 'is-editing' : ''}`}
              layout={rglLayout}
              cols={cols}
              rowHeight={ROW_HEIGHT}
              margin={[12, 12]}
              containerPadding={[0, 0]}
              isDraggable={editMode}
              isResizable={editMode}
              // Desktop: free positioning — tiles stay exactly where dropped.
              // Phone/tablet: vertical compaction so widgets stack without gaps
              // (free-positioning with clamped cols would leave large empty holes).
              compactType={cols < COLS_DESKTOP ? 'vertical' : null}
              preventCollision={cols >= COLS_DESKTOP}
              // No draggableHandle — in edit mode the whole widget is grabbable
              // (any pointerdown starts drag). Inner UI is muted via
              // pointer-events-none so widget content can't intercept drags.
              onLayoutChange={handleLayoutChange}
            >
              {state.widgets.map((w) => {
                const def = widgetDef(w.type)
                if (!def) return <div key={w.i} />
                const Render = def.Render
                // Chromeless widgets render edge-to-edge without the card frame.
                // In edit mode the inner content is pointer-disabled so RGL gets
                // every pointerdown for drag; the edit/remove buttons opt back
                // in via pointer-events-auto + stopPropagation in the widget.
                if (def.chromeless) {
                  // Chromeless: the widget is the tile. Outer div is just a
                  // positioning container; the widget renders its own rounded
                  // card filling the cell. overflow-visible lets iOS-jiggle
                  // edit buttons poke past the cell edge into the gutter.
                  // The "chromeless-cell" class disables RGL's dashed cell
                  // outline so we don't get a rectangle around the rounded tile.
                  return (
                    <div
                      key={w.i}
                      className={`chromeless-cell h-full w-full ${editMode ? 'cursor-grab active:cursor-grabbing' : ''}`}
                      style={{ overflow: 'visible' }}
                    >
                      <Render
                        config={w.config ?? {}}
                        onConfigChange={(next) => updateConfig(w.i, next)}
                        editMode={editMode}
                        onRemove={() => removeWidget(w.i)}
                      />
                    </div>
                  )
                }
                // Standard widget: rounded card + iOS-jiggle delete button at the
                // top-right corner. Outer wrapper allows overflow so the button
                // can poke past the card edge into the grid gutter.
                return (
                  <div
                    key={w.i}
                    className={`relative h-full w-full ${editMode ? 'cursor-grab active:cursor-grabbing' : ''}`}
                    style={{ overflow: 'visible' }}
                  >
                    <div
                      className={`h-full w-full rounded-2xl border border-border-subtle bg-bg-elevated/60 overflow-hidden flex flex-col ${editMode ? 'ring-1 ring-accent/30 ring-inset' : ''}`}
                    >
                      <header className="px-4 py-2 border-b border-border-subtle bg-bg-overlay/30">
                        <span className="text-xs uppercase tracking-wider text-text-tertiary">
                          {def.label}
                        </span>
                      </header>
                      <div
                        className={`flex-1 min-h-0 overflow-hidden p-3 ${editMode ? 'pointer-events-none select-none' : ''}`}
                      >
                        <WidgetShell minPixels={def.minPixels}>
                          <Render
                            config={w.config ?? {}}
                            onConfigChange={(next) => updateConfig(w.i, next)}
                            editMode={editMode}
                            onRemove={() => removeWidget(w.i)}
                          />
                        </WidgetShell>
                      </div>
                    </div>
                    {editMode && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeWidget(w.i)}
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        onTouchEnd={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          removeWidget(w.i)
                        }}
                        aria-label="Widget entfernen"
                        className="pointer-events-auto absolute -top-1 -right-1 h-5 w-5 touch:h-9 touch:w-9 touch:-top-2 touch:-right-2 rounded-full bg-bg-elevated hover:bg-bg-elevated text-text-tertiary hover:text-danger shadow-md ring-1 ring-border-default z-20 touch:opacity-100"
                      >
                        <X className="h-2.5 w-2.5 touch:h-4 touch:w-4" />
                      </Button>
                    )}
                  </div>
                )
              })}
            </ResponsiveGridLayout>
          </div>
        </div>
      )}

      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Widget hinzufügen"
        description="Wähle ein Widget aus dem Katalog. Mehrfachverwendung ist erlaubt."
        footer={
          <div className="flex justify-end">
            <Button variant="ghost" onClick={() => setPickerOpen(false)}>
              <X className="h-4 w-4" /> Abbrechen
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {listWidgets()
            .filter((def) => !allowedWidgets || (allowedWidgets[def.type] ?? true))
            .map((def) => {
              const Icon = def.icon
              return (
                <button
                  key={def.type}
                  type="button"
                  onClick={() => addWidget(def.type)}
                  className="text-left rounded-xl border border-border-subtle bg-bg-overlay/40 p-4 hover:border-accent/60 hover:bg-bg-overlay/60 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="h-4 w-4 text-accent" />
                    <span className="font-medium text-text-primary">{def.label}</span>
                  </div>
                  <p className="text-xs text-text-tertiary">{def.description}</p>
                  <p className="mt-2 text-2xs uppercase tracking-wider text-text-tertiary">
                    Standardgröße {def.defaultSize.w}×{def.defaultSize.h}
                  </p>
                </button>
              )
            })}
        </div>
      </Dialog>
    </div>
  )
}
